import React, { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import * as Icons from "@/assets/features/candidates";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import InputField from "@/components/shared/inputs/InputField";
import BaseModal from "@/components/shared/modals/BaseModal";
import SortableItem from "@/components/table/SortableItem";

const ColumnsModal = ({ open, onOpenChange, table }) => {
  const [search, setSearch] = useState("");
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [preview, setPreview] = useState([]);
  const allColumns = table?.getAllLeafColumns?.() ?? [];

  const actionsColumnId = React.useMemo(() => {
    const col = allColumns.find(col =>
      String(col.columnDef?.header).toLowerCase() === 'actions'
    );
    return col ? (col.id ?? col.columnDef?.accessorKey ?? String(col.columnDef?.header)) : null;
  }, [allColumns]);

  const orderWithActionsLast = React.useCallback((ids) => {
    if (!actionsColumnId || !ids.includes(actionsColumnId)) {
      return ids;
    }
    const filtered = ids.filter(id => id !== actionsColumnId);
    filtered.push(actionsColumnId);
    return filtered;
  }, [actionsColumnId]);

  useEffect(() => {
    if (!open) return;
    const visible = allColumns
      .filter((c) => typeof c.getIsVisible === "function" && c.getIsVisible())
      .map(
        (c) => c.id ?? c.columnDef?.accessorKey ?? String(c.columnDef?.header)
      );
    const orderedVisible = orderWithActionsLast(visible);
    setSelectedColumns(orderedVisible);
    setPreview(orderedVisible);
  }, [open, table, allColumns, orderWithActionsLast]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleToggle = (id, checked) => {
    const isChecked = checked === true || checked === "true";
    setSelectedColumns((prev) => {
      const next = isChecked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id);
      return orderWithActionsLast(next);
    });

    if (isChecked) {
      setPreview((prev) => orderWithActionsLast([...new Set([...prev, id])]));
    } else {
      setPreview((prev) => orderWithActionsLast(prev.filter((x) => x !== id)));
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setPreview((items) => {
        if (active.id === actionsColumnId) {
          return items;
        }
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        const moved = arrayMove(items, oldIndex, newIndex);
        return orderWithActionsLast(moved);
      });
    }
  };

  const handleSave = () => {
    const cols = table?.getAllLeafColumns?.() ?? [];
    cols.forEach((col) => {
      const colId =
        col.id ?? col.columnDef?.accessorKey ?? String(col.columnDef?.header);
      const shouldBeVisible = selectedColumns.includes(colId);
      if (
        typeof col.getIsVisible === "function" &&
        col.getIsVisible() !== shouldBeVisible
      ) {
        col.toggleVisibility?.(shouldBeVisible);
      }
    });

    if (typeof table?.setColumnOrder === "function") {
      table.setColumnOrder(preview);
    }

    onOpenChange(false);
  };

  return (
    <BaseModal
      open={open}
      onClose={() => onOpenChange(false)}
      onConfirm={handleSave} 
      title="Choose which columns to show"
      textBtn="Save"
      size="xl" 
    >

      <div className="grid grid-cols-3 gap-6 text-sm h-[60vh] overflow-y-auto scroll-y ">
        <div className="col-span-2">
          <div className="relative w-fit mt-2  mx-1">
            <InputField
              className="mb-4 px-8 rounded-[16px] shadow-light-sm w-48"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearch(e.currentTarget.value);
                }
              }}
            />
            <button
              type="button"
              className="absolute left-3 top-2.5 text-gray-500"
              onClick={() => {
                setSearch(search.trim());
              }}
            >
              <Icons.Search className="h-4 w-4" color="#909FBA" />
            </button>
          </div>

          <h1 className="text-md uppercase pb-2">Table Fields</h1>
          <div className="max-h-[390px] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              {allColumns
                .filter((col) => {
                  const header = String(col.columnDef?.header ?? "");
                  return header.toLowerCase().includes(search.toLowerCase());
                })
                .map((col) => {
                  const colId =
                    col.id ??
                    col.columnDef?.accessorKey ??
                    String(col.columnDef?.header);
                  return (
                    <div key={colId} className="flex items-center gap-2">
                      <Checkbox
                        disabled={!col.columnDef?.enableHiding}
                        checked={selectedColumns.includes(colId)}
                        onCheckedChange={(checked) =>
                          handleToggle(colId, checked)
                        }
                      />
                      <span
                        className={`${
                          !col.columnDef?.enableHiding ? "text-[#9797979E]" : ""
                        }`}
                      >
                        {String(col.columnDef?.header ?? colId)}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <h4 className="text-md font-medium mb-2">FIELDS PREVIEW</h4>
          <p className="text-xs text-gray-500 mb-3"></p>
          <div className="bg-[#F1F5FC] rounded-lg w-auto py-3 px-3 shadow-lg relative mb-5">
            <div className="absolute -top-2 left-4 w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-[#F1F5FC]" />
            <div className="flex items-start gap-2">
              <Icons.Feedback className="h-5 w-5" />
              <div className="text-xs flex flex-col gap-1">
                <strong> Info</strong>
                <p className="text-secondary">Drag the fields to rearrange</p>
              </div>
            </div>
          </div>

          <div className="h-[300px] overflow-x-auto scroll-y">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={preview}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {preview
                    .filter((id) => {
                      const col = allColumns.find(
                        (c) =>
                          c.id === id ||
                          c.columnDef?.accessorKey === id ||
                          String(c.columnDef?.header) === id
                      );
                      const header = String(col?.columnDef?.header ?? id);
                      return header.toLowerCase().includes(search.toLowerCase());
                    })
                    .map((id) => {
                      const col = allColumns.find(
                        (c) =>
                          c.id === id ||
                          c.columnDef?.accessorKey === id ||
                          String(c.columnDef?.header) === id
                      );
                      return (
                        <SortableItem
                          key={id}
                          id={id}
                          label={String(col?.columnDef?.header ?? id)}
                        />
                      );
                    })}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default ColumnsModal;